<template>
  <div v-if="visible" class="modal-overlay">
    <div class="modal-content">
      <div class="modal-header">
        <h3>
          {{
            isEditing
              ? readOnly
                ? s__('CalendarEvent|Event Details')
                : s__('CalendarEvent|Edit Event')
              : s__('CalendarEvent|New Event')
          }}
        </h3>
        <div class="header-actions">
          <button
            v-if="isEditing && !readOnly"
            class="btn-delete-header"
            :disabled="loading"
            :title="s__('CalendarEvent|Delete')"
            @click="confirmDelete"
          >
            {{ s__('CalendarEvent|Delete') }}
          </button>
          <button class="modal-close" aria-label="Close" @click="close">
            <CloseIcon />
          </button>
        </div>
      </div>

      <div class="modal-body">
        <div
          v-if="isEditing && !readOnly && eventHasEnded"
          class="calendar-ended-notice"
          role="status"
        >
          {{ s__('CalendarEvent|This event already ended') }}
        </div>
        <div v-if="isEditing && event?.detached" class="calendar-detached-notice" role="status">
          <div class="calendar-detached-notice-title">
            {{ s__('CalendarEvent|Detached occurrence') }}
          </div>
          <div class="calendar-detached-notice-body">
            {{
              s__(
                'CalendarEvent|This occurrence was detached from the series. Future changes that apply to the whole series will not affect it.'
              )
            }}
          </div>
        </div>
        <!-- Title -->
        <div class="form-group">
          <label class="form-label">{{ s__('CalendarEvent|Title') }} *</label>
          <input
            v-model="form.title"
            type="text"
            class="form-input"
            :placeholder="s__('CalendarEvent|Event title')"
            :disabled="readOnly"
            required
          />
        </div>

        <!-- Project Selection -->
        <div class="form-group">
          <label class="form-label">{{ s__('CalendarEvent|Project') }}</label>
          <select v-model="form.projectId" class="form-select" :disabled="readOnly">
            <option :value="null">{{ s__('CalendarEvent|Personal event') }}</option>
            <option v-for="project in projects" :key="project.id" :value="project.id">
              {{ project.title }}
            </option>
          </select>
        </div>

        <!-- All Day Toggle -->
        <div class="form-group">
          <label class="checkbox-label">
            <input v-model="form.allDay" type="checkbox" :disabled="readOnly" />
            <span>{{ s__('CalendarEvent|All day') }}</span>
          </label>
        </div>

        <!-- Recurring Series (create only) -->
        <div v-if="!isEditing" class="form-group">
          <label class="checkbox-label">
            <input
              v-model="form.recurringEnabled"
              type="checkbox"
              :disabled="readOnly"
              data-testid="calendar-recurring-toggle"
            />
            <span>{{ s__('CalendarEvent|Recurring series') }}</span>
          </label>
        </div>

        <div v-if="!isEditing && form.recurringEnabled" class="form-group recurrence-fields">
          <label class="form-label">{{ s__('CalendarEvent|Series range') }}</label>
          <div class="recurrence-range">
            <DateTimeInput
              class="datetime-input-field recurrence-date-field"
              mode="date"
              :date="form.rangeStartDate"
              :disabled="readOnly"
              :locale="locale"
              :title="s__('CalendarEvent|Start')"
              data-testid="calendar-series-range-start"
              @update:date="form.rangeStartDate = $event || ''"
            />
            <span class="recurrence-range-separator">-</span>
            <DateTimeInput
              class="datetime-input-field recurrence-date-field recurrence-range-end-input"
              mode="date"
              :date="form.rangeEndDate"
              :disabled="readOnly"
              :locale="locale"
              :title="s__('CalendarEvent|End')"
              data-testid="calendar-series-range-end"
              @update:date="form.rangeEndDate = $event || ''"
            />
          </div>
          <div class="weekday-selector">
            <label v-for="weekday in weekdayOptions" :key="weekday.value" class="checkbox-label">
              <input
                :checked="form.weekdays.includes(weekday.value)"
                type="checkbox"
                :disabled="readOnly"
                :data-testid="`calendar-series-weekday-${weekday.value}`"
                @change="toggleWeekday(weekday.value)"
              />
              <span>{{ weekday.label }}</span>
            </label>
          </div>
          <div v-if="recurrenceError" class="error" style="margin-top: 4px; font-size: 13px">
            {{ recurrenceError }}
          </div>
        </div>

        <!-- Start Date/Time -->
        <div class="form-group">
          <label class="form-label">{{ s__('CalendarEvent|Start') }} *</label>
          <DateTimeInput
            v-if="!form.allDay"
            class="datetime-input-field"
            :datetime="form.startAt"
            :disabled="readOnly"
            :locale="locale"
            @update:datetime="form.startAt = $event"
          />
          <input
            v-else
            v-model="form.startDate"
            type="date"
            class="form-input date-input all-day-date-input"
            :disabled="readOnly"
            required
          />
        </div>

        <!-- End Date/Time -->
        <div v-if="!form.allDay" class="form-group">
          <label class="form-label">{{ s__('CalendarEvent|End') }}</label>
          <DateTimeInput
            class="datetime-input-field"
            :datetime="form.endAt"
            :disabled="readOnly"
            :locale="locale"
            :title="s__('CalendarEvent|End')"
            @update:datetime="form.endAt = $event"
          />
          <div v-if="endTimeError" class="error" style="margin-top: 4px; font-size: 13px">
            {{ endTimeError }}
          </div>
        </div>

        <!-- Location -->
        <div class="form-group">
          <label class="form-label">{{ s__('CalendarEvent|Location') }}</label>
          <input
            v-model="form.location"
            type="text"
            class="form-input"
            :placeholder="s__('CalendarEvent|Add location')"
            :disabled="readOnly"
          />
        </div>

        <!-- Color -->
        <div class="form-group" v-if="!readOnly">
          <label class="form-label">{{ s__('CalendarEvent|Color') }}</label>
          <div class="color-picker">
            <div class="color-selected">
              <label class="color-selected-label">{{ s__('CalendarEvent|Selected') }}</label>
              <div
                class="color-selected-display"
                :style="{ backgroundColor: getColorHex(form.color) }"
                :title="getColorHex(form.color)"
              ></div>
            </div>
            <div class="color-options">
              <button
                v-for="color in colors"
                :key="color.key"
                :class="['color-btn', { selected: isColorSelected(color) }]"
                :style="{ backgroundColor: color.hex }"
                :title="colorLabel(color.key)"
                @click="selectColor(color)"
              ></button>
              <div class="color-custom">
                <input
                  v-model="customColor"
                  type="color"
                  :title="s__('CalendarEvent|Custom color')"
                  @input="selectCustomColor"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Organizer (only show when editing existing event) -->
        <div v-if="isEditing && organizer" class="form-group">
          <label class="form-label">{{ s__('CalendarEvent|Organizer') }}</label>
          <div class="organizer-info">
            <div class="organizer-name-row">
              <span class="organizer-username">{{
                organizer.username ? '@' + organizer.username : ''
              }}</span>
            </div>
            <span v-if="organizer.name" class="organizer-name">{{ organizer.name }}</span>
            <span v-if="!organizer.username && !organizer.name" class="organizer-fallback"
              >User {{ organizer.id }}</span
            >
          </div>
        </div>

        <!-- Participants -->
        <div class="form-group">
          <label class="form-label">{{ s__('CalendarEvent|Participants') }}</label>
          <div class="participants-list">
            <div
              v-for="participant in selectedParticipants"
              :key="participant.id"
              class="participant-item"
            >
              <div class="participant-info">
                <div class="participant-name-row">
                  <!-- Status indicator dot (only for organizer) -->
                  <span
                    v-if="isOrganizer && participant._responseStatus"
                    :class="[
                      'participant-status-dot',
                      `participant-status-${participant._responseStatus}`,
                    ]"
                    :title="getStatusTitle(participant._responseStatus)"
                  ></span>
                  <span class="participant-username">{{
                    participant.username ? '@' + participant.username : ''
                  }}</span>
                </div>
                <span v-if="participant.name" class="participant-name">{{ participant.name }}</span>
                <span v-if="!participant.username && !participant.name" class="participant-fallback"
                  >User {{ participant.id }}</span
                >
              </div>
              <button
                v-if="!readOnly"
                class="participant-remove"
                :title="s__('CalendarEvent|Remove participant')"
                @click.stop="removeParticipant(participant.id)"
              >
                &times;
              </button>
            </div>
            <div v-if="selectedParticipants.length === 0" class="no-participants">
              {{ s__('CalendarEvent|No participants added') }}
            </div>
          </div>
          <div v-if="!readOnly" class="add-participant">
            <user-select
              :key="`user-select-${props.visible ? 'open' : 'closed'}-${selectedParticipants.length}`"
              v-model="selectedUserToAdd"
              :users="availableUsers"
              :placeholder="s__('CalendarEvent|Add participants...')"
              :show-empty-option="false"
              :searchable="true"
              :local-search="!!form.projectId"
            />
          </div>
        </div>

        <!-- Reminders -->
        <div class="form-group">
          <label class="form-label">{{ s__('CalendarEvent|Reminders') }}</label>
          <div class="reminders-list">
            <div
              v-for="(reminder, index) in form.reminderMinutes"
              :key="index"
              class="reminder-item"
            >
              <span class="reminder-text">{{ reminderLabel(reminder) }}</span>
              <button
                v-if="!readOnly"
                class="reminder-remove"
                :title="s__('CalendarEvent|Remove reminder')"
                @click="removeReminder(index)"
              >
                &times;
              </button>
            </div>
            <div v-if="form.reminderMinutes.length === 0" class="no-reminders">
              {{ s__('CalendarEvent|No reminders set') }}
            </div>
          </div>
          <div v-if="!readOnly" class="add-reminder">
            <select v-model="selectedReminder" class="form-select reminder-select">
              <option :value="null" disabled>{{ s__('CalendarEvent|Add reminder...') }}</option>
              <option
                v-for="option in availableReminderOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- Description -->
        <div class="form-group">
          <label class="form-label">{{ s__('CalendarEvent|Description') }}</label>
          <textarea
            v-model="form.description"
            class="form-textarea"
            rows="3"
            :placeholder="s__('CalendarEvent|Add description')"
            :disabled="readOnly"
          ></textarea>
        </div>

        <!-- Attachments (only for existing events) -->
        <!-- Temporarily removed - will be added back after testing task attachments -->
        <!--
        <div v-if="isEditing && event?.id" class="form-group">
          <label class="form-label">{{ s__('CalendarEvent|Attachments') }}</label>
          <CalendarEventAttachments
            :event-id="event.id"
            :read-only="readOnly"
            :current-user-id="currentUser?.id"
            :event-author-id="event?.author?.id"
            @uploaded="handleAttachmentsChanged"
            @deleted="handleAttachmentsChanged"
          />
        </div>
        -->

        <div v-if="error" class="error-message">{{ error }}</div>
      </div>

      <div class="modal-footer">
        <!-- Accept/Reject buttons for participants (not organizer) -->
        <div v-if="showResponseButtons" class="footer-left">
          <button
            v-if="showAcceptButton"
            class="btn-accept"
            :aria-label="s__('CalendarEvent|Accept')"
            :disabled="responding"
            @click="handleAccept"
          >
            <span class="response-btn-label">{{ s__('CalendarEvent|Accept') }}</span>
            <span class="response-btn-icon" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path
                  d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0"
                />
              </svg>
            </span>
          </button>
          <button
            v-if="showRejectButton"
            class="btn-reject"
            :aria-label="s__('CalendarEvent|Reject')"
            :disabled="responding"
            @click="handleReject"
          >
            <span class="response-btn-label">{{ s__('CalendarEvent|Reject') }}</span>
            <span class="response-btn-icon" aria-hidden="true">
              <CloseIcon :size="14" color="currentColor" />
            </span>
          </button>
        </div>
        <div class="footer-right">
          <button class="btn-sm btn-secondary" :disabled="loading" @click="close">
            {{ readOnly ? __('Close') : __('Cancel') }}
          </button>
          <button
            v-if="!readOnly"
            class="btn-sm btn-primary"
            :disabled="loading || !isValid"
            @click="submit"
          >
            {{ loading ? __('Saving…') : s__('CalendarEvent|Save') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-if="showSeriesScopeSelector" class="scope-selector-overlay">
    <div class="scope-selector-modal" data-testid="calendar-series-scope-selector">
      <h4 class="scope-selector-title">{{ scopeSelectorTitle }}</h4>
      <div class="scope-selector-actions">
        <button
          class="btn-sm btn-secondary"
          data-testid="calendar-series-scope-single"
          @click="resolveScope('single')"
        >
          <span class="scope-selector-action-label">{{
            s__('CalendarEvent|Only this event')
          }}</span>
          <span class="scope-selector-action-hint">{{ singleScopeHint }}</span>
        </button>
        <button
          v-if="showFollowingScopeOption"
          class="btn-sm btn-secondary"
          data-testid="calendar-series-scope-following"
          @click="resolveScope('following')"
        >
          <span class="scope-selector-action-label">{{
            s__('CalendarEvent|This and following events')
          }}</span>
          <span class="scope-selector-action-hint">{{ followingScopeHint }}</span>
        </button>
        <button
          v-if="showAllScopeOption"
          class="btn-sm btn-secondary"
          data-testid="calendar-series-scope-all"
          @click="resolveScope('all')"
        >
          <span class="scope-selector-action-label">{{
            s__('CalendarEvent|All events in series')
          }}</span>
          <span class="scope-selector-action-hint">{{ allScopeHint }}</span>
        </button>
        <button
          v-if="showSeriesScopeOption"
          class="btn-sm btn-secondary"
          data-testid="calendar-series-scope-series"
          @click="resolveScope('series')"
        >
          <span class="scope-selector-action-label">{{ s__('CalendarEvent|Whole series') }}</span>
          <span class="scope-selector-action-hint">{{ seriesScopeHint }}</span>
        </button>
      </div>
      <div class="scope-selector-footer">
        <button
          class="btn-sm btn-secondary"
          data-testid="calendar-series-scope-cancel"
          @click="resolveScope(null)"
        >
          {{ __('Cancel') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useMutation, useQuery, useLazyQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { __, s__ } from '~/locale'
import UserSelect from '@/components/common/UserSelect.vue'
import DateTimeInput from '@/components/common/DateTimeInput.vue'
import CloseIcon from '@/components/common/icons/CloseIcon.vue'
import CalendarEventAttachments from './CalendarEventAttachments.vue'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'
import { useCurrentUser } from '@/composables/useCurrentUser'

const props = defineProps({
  visible: { type: Boolean, default: false },
  event: { type: Object, default: null }, // Existing event for editing
  projects: { type: Array, default: () => [] },
  initialDate: { type: Date, default: null },
  locale: { type: String, default: null },
  timezone: { type: String, default: null },
  readOnly: { type: Boolean, default: false }, // Read-only mode when user can't edit
})

const emit = defineEmits(['close', 'saved', 'deleted', 'responded'])

// Lock body scroll when modal is open
useBodyScrollLock(computed(() => props.visible))

const colors = [
  { key: 'blue', hex: '#4285f4' },
  { key: 'green', hex: '#0f9d58' },
  { key: 'red', hex: '#db4437' },
  { key: 'yellow', hex: '#f4b400' },
  { key: 'purple', hex: '#9c27b0' },
  { key: 'teal', hex: '#009688' },
  { key: 'orange', hex: '#ff5722' },
  { key: 'pink', hex: '#e91e63' },
  { key: 'indigo', hex: '#3f51b5' },
  { key: 'cyan', hex: '#00bcd4' },
  { key: 'brown', hex: '#795548' },
  { key: 'gray', hex: '#607d8b' },
]

const colorLabels = {
  blue: 'CalendarEvent|Blue',
  green: 'CalendarEvent|Green',
  red: 'CalendarEvent|Red',
  yellow: 'CalendarEvent|Yellow',
  purple: 'CalendarEvent|Purple',
  teal: 'CalendarEvent|Teal',
  orange: 'CalendarEvent|Orange',
  pink: 'CalendarEvent|Pink',
  indigo: 'CalendarEvent|Indigo',
  cyan: 'CalendarEvent|Cyan',
  brown: 'CalendarEvent|Brown',
  gray: 'CalendarEvent|Gray',
}

function colorLabel(key) {
  return s__(colorLabels[key] || key)
}

// Custom color handling
const customColor = ref('#4285f4')

// Get hex value from color (either key or hex)
function getColorHex(colorValue) {
  if (!colorValue) return '#4285f4'
  // If it's already a hex value, return it
  if (colorValue.startsWith('#')) {
    return colorValue
  }
  // Otherwise, find the hex value from colors array
  const color = colors.find(c => c.key === colorValue)
  return color ? color.hex : '#4285f4'
}

// Check if a color is selected (either by key or hex match)
function isColorSelected(color) {
  const currentHex = getColorHex(form.value.color)
  return currentHex.toLowerCase() === color.hex.toLowerCase()
}

// Select a predefined color
function selectColor(color) {
  form.value.color = color.hex
  // Don't update customColor - it should only change when user picks a custom color
}

// Select a custom color
function selectCustomColor(event) {
  const hex = event.target.value
  form.value.color = hex
  customColor.value = hex
}

const reminderOptions = [
  { value: 5, label: s__('CalendarEvent|5 minutes before') },
  { value: 15, label: s__('CalendarEvent|15 minutes before') },
  { value: 30, label: s__('CalendarEvent|30 minutes before') },
  { value: 60, label: s__('CalendarEvent|1 hour before') },
  { value: 120, label: s__('CalendarEvent|2 hours before') },
  { value: 1440, label: s__('CalendarEvent|1 day before') },
  { value: 2880, label: s__('CalendarEvent|2 days before') },
]

const form = ref({
  title: '',
  projectId: null,
  allDay: false,
  recurringEnabled: false,
  rangeStartDate: '',
  rangeEndDate: '',
  weekdays: [1, 2, 3, 4, 5],
  startDate: '', // Used only for allDay events
  startAt: null, // ISO datetime string for non-allDay events
  endAt: null, // ISO datetime string for non-allDay events
  location: '',
  color: '#4285f4',
  description: '',
  reminderMinutes: [],
  participantIds: [],
})

const selectedReminder = ref(null)
const selectedUserToAdd = ref(null)
const selectedParticipants = ref([]) // Array of user objects for display
const removedParticipantIds = ref(new Set()) // Track IDs of participants that were removed
const error = ref(null)
const loading = ref(false)
const showScopeSelector = ref(false)
let scopeResolver = null
const scopeSelectorActionType = ref('update')

const availableReminderOptions = computed(() => {
  return reminderOptions.filter(opt => !form.value.reminderMinutes.includes(opt.value))
})

function reminderLabel(minutes) {
  const option = reminderOptions.find(opt => opt.value === minutes)
  return option ? option.label : `${minutes} minutes before`
}

function addReminder() {
  if (
    selectedReminder.value !== null &&
    !form.value.reminderMinutes.includes(selectedReminder.value)
  ) {
    const valueToAdd = selectedReminder.value
    form.value.reminderMinutes.push(valueToAdd)
    form.value.reminderMinutes.sort((a, b) => b - a) // Sort descending (longest first)
    // Use nextTick to ensure DOM updates before resetting the dropdown
    nextTick(() => {
      selectedReminder.value = null
    })
  }
}

// Auto-add reminder when user selects a time
watch(selectedReminder, newValue => {
  if (newValue !== null) {
    addReminder()
  }
})

function removeReminder(index) {
  form.value.reminderMinutes.splice(index, 1)
}

// Participant management
function addParticipant() {
  if (!selectedUserToAdd.value) {
    return
  }

  const userId = String(selectedUserToAdd.value)

  // Double-check to prevent duplicates
  if (isParticipantSelected(userId)) {
    // Clear selection and return
    nextTick(() => {
      selectedUserToAdd.value = null
    })
    return
  }

  // Find user in available users first
  const user = availableUsers.value.find(u => String(u.id) === userId)

  if (user) {
    // User found in available users (project members)
    // When adding a participant during editing, always treat them as new (pending)
    // Don't use old status from eventParticipants - they're being re-invited
    const participantToAdd = {
      ...user,
      _responseStatus: 'pending', // Always pending for newly added participants
    }
    selectedParticipants.value.push(participantToAdd)
    // Note: We don't remove from removedParticipantIds here - we'll use it when submitting
    // to determine if this participant should be in resendParticipantIds
    syncParticipantIds()
    // Clear selection after adding - use nextTick to ensure UserSelect processes it
    nextTick(() => {
      selectedUserToAdd.value = null
    })
  } else {
    // User not in available users (from backend search for personal events)
    // Load user by ID - the watcher will add it when result arrives
    // selectedUserToAdd.value is already set to userId, so the query will use it
    try {
      loadUserById()
    } catch (error) {
      // Silently handle errors - user will see the issue if query fails
    }
  }
}

// Sync form.value.participantIds with selectedParticipants (single source of truth)
function syncParticipantIds() {
  const ids = selectedParticipants.value.filter(p => p && p.id).map(p => String(p.id))
  form.value.participantIds = ids
}

// Helper to get participant status from eventParticipants when available
function getParticipantStatus(userId) {
  if (!props.event || !props.event.eventParticipants) return null
  const ep = props.event.eventParticipants.find(p => p.user && String(p.user.id) === String(userId))
  return ep?.responseStatus || null
}

// Watch for selectedUserToAdd changes and auto-add participant when user is selected
watch(
  () => selectedUserToAdd.value,
  (newValue, oldValue) => {
    // Skip if modal is not visible or initializing
    if (!props.visible || isInitializing.value) {
      return
    }

    // Only process if we have a new value (not null/undefined) and it's different from the old one
    // This prevents issues when the value is reset to null
    if (newValue && newValue !== oldValue) {
      const userId = String(newValue)

      // Check if already selected - if so, just clear the selection
      if (isParticipantSelected(userId)) {
        nextTick(() => {
          selectedUserToAdd.value = null
        })
        return
      }

      // Add the participant
      addParticipant()
    }
  },
  { immediate: false }
)

function removeParticipant(userId) {
  const index = selectedParticipants.value.findIndex(
    p => p && p.id && String(p.id) === String(userId)
  )
  if (index !== -1) {
    selectedParticipants.value.splice(index, 1)
    // Track that this participant was removed (so we can resend invitation if re-added)
    removedParticipantIds.value.add(String(userId))
    syncParticipantIds()
    // Force reactivity update
    nextTick(() => {
      // Ensure the list is updated
    })
  }
}

function isParticipantSelected(userId) {
  // Check selectedParticipants (single source of truth) instead of form.value.participantIds
  return selectedParticipants.value.some(p => String(p.id) === String(userId))
}

// Get status title for tooltip
function getStatusTitle(status) {
  switch (status) {
    case 'accepted':
      return s__('CalendarEvent|Accepted')
    case 'declined':
      return s__('CalendarEvent|Declined')
    case 'pending':
      return s__('CalendarEvent|Pending')
    default:
      return ''
  }
}

const isEditing = computed(() => !!props.event)
const showFollowingScopeOption = computed(() => scopeSelectorActionType.value !== 'respond')
const showAllScopeOption = computed(() => scopeSelectorActionType.value !== 'respond')
const showSeriesScopeOption = computed(() => scopeSelectorActionType.value === 'respond')
const scopeSelectorTitle = computed(() => {
  if (scopeSelectorActionType.value === 'delete') {
    return s__('CalendarEvent|Apply deletion to:')
  }
  if (scopeSelectorActionType.value === 'respond') {
    return s__('CalendarEvent|Apply response to:')
  }

  return s__('CalendarEvent|Apply update to:')
})

const singleScopeHint = computed(() => {
  if (scopeSelectorActionType.value === 'delete') {
    return s__('CalendarEvent|Delete only this occurrence. Other events stay unchanged.')
  }
  if (scopeSelectorActionType.value === 'respond') {
    return s__('CalendarEvent|Apply your response only to this event.')
  }

  return s__(
    'CalendarEvent|Update only this occurrence. It becomes detached and will not receive future series updates.'
  )
})

const followingScopeHint = computed(() => {
  if (scopeSelectorActionType.value === 'delete') {
    return s__('CalendarEvent|Delete this and all future occurrences from this series.')
  }

  return s__(
    'CalendarEvent|Update this and all future occurrences. A new series branch starts from this date.'
  )
})

const allScopeHint = computed(() => {
  if (scopeSelectorActionType.value === 'delete') {
    return s__('CalendarEvent|Delete the whole series and all its occurrences.')
  }
  if (scopeSelectorActionType.value === 'respond') {
    return ''
  }

  return s__('CalendarEvent|Update all occurrences in this series.')
})

const seriesScopeHint = computed(() =>
  s__('CalendarEvent|Apply your response to all non-detached events in this series.')
)

const weekdayOptions = [
  { value: 1, label: s__('CalendarEvent|Mon') },
  { value: 2, label: s__('CalendarEvent|Tue') },
  { value: 3, label: s__('CalendarEvent|Wed') },
  { value: 4, label: s__('CalendarEvent|Thu') },
  { value: 5, label: s__('CalendarEvent|Fri') },
  { value: 6, label: s__('CalendarEvent|Sat') },
  { value: 0, label: s__('CalendarEvent|Sun') },
]

function toggleWeekday(weekday) {
  if (form.value.weekdays.includes(weekday)) {
    form.value.weekdays = form.value.weekdays.filter(day => day !== weekday)
  } else {
    form.value.weekdays = [...form.value.weekdays, weekday].sort((a, b) => a - b)
  }
}

const recurrenceError = computed(() => {
  if (!form.value.recurringEnabled || isEditing.value) return null
  if (!form.value.rangeStartDate || !form.value.rangeEndDate) {
    return s__('CalendarEvent|Series start and end dates are required')
  }

  const start = new Date(form.value.rangeStartDate)
  const end = new Date(form.value.rangeEndDate)
  if (end < start) {
    return s__('CalendarEvent|Series end date must be on or after start date')
  }

  const diffMs = end.getTime() - start.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays > 90) {
    return s__('CalendarEvent|Series range cannot exceed 90 days')
  }

  if (!form.value.weekdays || form.value.weekdays.length === 0) {
    return s__('CalendarEvent|Select at least one weekday')
  }

  return null
})

const isValid = computed(() => {
  const hasStart = form.value.allDay ? form.value.startDate : form.value.startAt
  return form.value.title.trim() && hasStart && !endTimeError.value && !recurrenceError.value
})

const endTimeError = computed(() => {
  if (form.value.allDay || !form.value.startAt || !form.value.endAt) {
    return null
  }

  // Compare start and end datetime
  const startDateTime = new Date(form.value.startAt)
  const endDateTime = new Date(form.value.endAt)

  if (endDateTime <= startDateTime) {
    return s__('CalendarEvent|End time must be later than start time')
  }

  return null
})

// Get current user's participant info
const currentUserParticipant = computed(() => {
  if (!props.event || !userId.value) return null
  return props.event.currentUserParticipant || null
})

// Align with server CalendarEvent#ended? (all-day / no end_at / in-progress)
const eventHasEnded = computed(() => {
  if (!props.event) return false
  if (props.event.ended === true) return true
  if (props.event.ended === false) return false
  if (!props.event.endAt) {
    if (props.event.allDay && props.event.startAt) {
      const start = new Date(props.event.startAt)
      const today = new Date()
      start.setHours(0, 0, 0, 0)
      today.setHours(0, 0, 0, 0)
      return start < today
    }
    return false
  }
  const endAt = new Date(props.event.endAt)
  return endAt < new Date()
})

// Recurring series scope (update/delete/respond) only for non-detached, not-yet-ended occurrences
const shouldOfferSeriesScope = computed(
  () => !!props.event?.seriesId && !props.event?.detached && !eventHasEnded.value
)

const showSeriesScopeSelector = computed(
  () => showScopeSelector.value && shouldOfferSeriesScope.value
)

// Check if user is organizer
const isOrganizer = computed(() => {
  if (!currentUserParticipant.value) return false
  return currentUserParticipant.value.role === 'organizer'
})

// Get organizer from event participants
const organizer = computed(() => {
  if (!props.event || !props.event.eventParticipants) return null
  const organizerParticipant = props.event.eventParticipants.find(
    ep => ep.user && ep.role === 'organizer'
  )
  return organizerParticipant?.user || null
})

// Show response buttons if:
// 1. User is a participant (not organizer)
// 2. Event hasn't ended
// 3. We're in edit mode (viewing an existing event)
const showResponseButtons = computed(() => {
  if (!isEditing.value) return false
  if (!currentUserParticipant.value) return false // User must be a participant
  if (isOrganizer.value) return false
  if (eventHasEnded.value) return false
  return true
})

// Show Accept button if status is pending or declined
const showAcceptButton = computed(() => {
  if (!showResponseButtons.value || !currentUserParticipant.value) return false
  const status = currentUserParticipant.value.responseStatus
  return status === 'pending' || status === 'declined'
})

// Show Reject button if status is pending or accepted
const showRejectButton = computed(() => {
  if (!showResponseButtons.value || !currentUserParticipant.value) return false
  const status = currentUserParticipant.value.responseStatus
  return status === 'pending' || status === 'accepted'
})

// Flag to prevent infinite loops when auto-updating end from start
const isUpdatingEndFromStart = ref(false)
const isInitializing = ref(false)
const isTogglingAllDay = ref(false)

// Handle allDay toggle - convert between date-only and datetime formats
watch(
  () => form.value.allDay,
  (newAllDay, oldAllDay) => {
    if (isInitializing.value || isTogglingAllDay.value) return
    if (newAllDay === oldAllDay) return

    isTogglingAllDay.value = true

    if (newAllDay) {
      // Switching to allDay - extract date from startAt
      if (form.value.startAt) {
        try {
          const date = new Date(form.value.startAt)
          const year = date.getFullYear()
          const month = String(date.getMonth() + 1).padStart(2, '0')
          const day = String(date.getDate()).padStart(2, '0')
          form.value.startDate = `${year}-${month}-${day}`
        } catch {
          // Invalid date, keep existing startDate
        }
      }
      // Clear datetime fields
      form.value.startAt = null
      form.value.endAt = null
    } else {
      // Switching from allDay - convert startDate to datetime
      if (form.value.startDate) {
        try {
          const startDateTime = new Date(`${form.value.startDate}T09:00:00`)
          form.value.startAt = startDateTime.toISOString()

          // Set end time to 1 hour after start
          const endDateTime = new Date(startDateTime.getTime() + 60 * 60 * 1000)
          form.value.endAt = endDateTime.toISOString()
        } catch {
          // Invalid date, set defaults
          const now = new Date()
          now.setHours(9, 0, 0, 0)
          form.value.startAt = now.toISOString()
          const endTime = new Date(now.getTime() + 60 * 60 * 1000)
          form.value.endAt = endTime.toISOString()
        }
      }
    }

    isTogglingAllDay.value = false
  }
)

// Auto-update end time when start time changes (start time + 1 hour)
watch(
  () => form.value.startAt,
  newStartAt => {
    if (
      !isUpdatingEndFromStart.value &&
      !isInitializing.value &&
      !isTogglingAllDay.value &&
      !form.value.allDay &&
      newStartAt
    ) {
      isUpdatingEndFromStart.value = true

      try {
        const startDateTime = new Date(newStartAt)
        // Calculate end time: start time + 1 hour
        const endDateTime = new Date(startDateTime.getTime() + 60 * 60 * 1000)
        form.value.endAt = endDateTime.toISOString()
      } catch (e) {
        // Invalid date, skip update
      }

      isUpdatingEndFromStart.value = false
    }
  }
)

// Initialize form when modal opens
watch(
  () => props.visible,
  visible => {
    if (visible) {
      isInitializing.value = true
      selectedReminder.value = null
      // Reset participant selection immediately
      selectedUserToAdd.value = null
      // Clear selectedParticipants when modal opens (important for subsequent opens)
      selectedParticipants.value = []

      if (props.event) {
        // Edit mode - populate from existing event
        const startAt = new Date(props.event.startAt)
        const endAt = props.event.endAt ? new Date(props.event.endAt) : null

        // Handle reminderMinutes - GraphQL returns camelCase, but check both
        const reminderMinutes = props.event.reminderMinutes || props.event.reminder_minutes || []

        // Load existing participants
        const existingParticipants = props.event.eventParticipants || props.event.participants || []
        const participantIds = existingParticipants
          .filter(ep => ep.user && ep.role !== 'organizer') // Exclude organizer (author)
          .map(ep => ep.user.id)

        // Convert to ISO strings for non-allDay events
        const startAtISO = props.event.allDay ? null : props.event.startAt
        const endAtISO = props.event.allDay || !endAt ? null : endAt.toISOString()
        const startDateOnly = props.event.allDay
          ? (() => {
              try {
                const date = new Date(props.event.startAt)
                const year = date.getFullYear()
                const month = String(date.getMonth() + 1).padStart(2, '0')
                const day = String(date.getDate()).padStart(2, '0')
                return `${year}-${month}-${day}`
              } catch {
                return ''
              }
            })()
          : ''

        form.value = {
          title: props.event.title || '',
          projectId: props.event.project?.id || null,
          allDay: props.event.allDay || false,
          recurringEnabled: false,
          rangeStartDate: '',
          rangeEndDate: '',
          weekdays: [1, 2, 3, 4, 5],
          startDate: startDateOnly,
          startAt: startAtISO,
          endAt: endAtISO,
          location: props.event.location || '',
          color: props.event.color || '#4285f4',
          description: props.event.description || '',
          reminderMinutes: Array.isArray(reminderMinutes) ? [...reminderMinutes] : [],
          participantIds: participantIds.map(id => String(id)),
        }

        // Set selected participants for display
        // Store user object with responseStatus for status indicators
        selectedParticipants.value = existingParticipants
          .filter(ep => ep.user && ep.role !== 'organizer')
          .map(ep => ({
            ...ep.user,
            _responseStatus: ep.responseStatus, // Store status for organizer view
            _participantId: ep.id, // Store participant ID for reference
          }))

        // Reset removed participants tracking when loading event
        removedParticipantIds.value.clear()

        // Sync participantIds from selectedParticipants
        syncParticipantIds()

        // Initialize custom color from form color
        customColor.value = getColorHex(form.value.color)
      } else {
        // Create mode - reset form with default 15-minute reminder
        // Calculate next hour from current time
        const now = new Date()

        // Round up to next hour
        const nextHour = new Date(now)
        nextHour.setHours(now.getHours() + 1)
        nextHour.setMinutes(0)
        nextHour.setSeconds(0)
        nextHour.setMilliseconds(0)

        // End time is 1 hour after start
        const endTime = new Date(nextHour)
        endTime.setHours(nextHour.getHours() + 1)

        // Format date for allDay field (YYYY-MM-DD)
        const year = nextHour.getFullYear()
        const month = String(nextHour.getMonth() + 1).padStart(2, '0')
        const day = String(nextHour.getDate()).padStart(2, '0')
        const initialDateStr = `${year}-${month}-${day}`

        form.value = {
          title: '',
          projectId: null,
          allDay: false,
          recurringEnabled: false,
          rangeStartDate: initialDateStr,
          rangeEndDate: initialDateStr,
          weekdays: [1, 2, 3, 4, 5],
          startDate: initialDateStr,
          startAt: nextHour.toISOString(),
          endAt: endTime.toISOString(),
          location: '',
          color: '#4285f4',
          description: '',
          reminderMinutes: [15], // Default reminder
          participantIds: [],
        }

        selectedParticipants.value = []
      }

      // Initialize custom color from form color
      customColor.value = getColorHex(form.value.color)

      error.value = null
      loading.value = false
      isInitializing.value = false
    } else {
      // Modal closed - clear all participant state to prevent stale data
      selectedParticipants.value = []
      selectedUserToAdd.value = null
      form.value.participantIds = []
    }
  }
)

function buildStartAt() {
  if (form.value.allDay) {
    return `${form.value.startDate}T00:00:00`
  }
  return form.value.startAt
}

function buildEndAt() {
  if (form.value.allDay || !form.value.endAt) {
    return null
  }
  return form.value.endAt
}

function close() {
  emit('close')
}

// GraphQL mutations
const GET_PROJECT_MEMBERS = gql`
  query GetProjectMembers($projectId: ID!) {
    project(id: $projectId) {
      id
      members {
        id
        username
        name
      }
    }
  }
`

const GET_USER_BY_ID = gql`
  query GetUserById($id: ID!) {
    user(id: $id) {
      id
      username
      name
    }
  }
`

const CREATE_EVENT = gql`
  mutation CreateCalendarEvent(
    $projectId: ID
    $title: String!
    $description: String
    $startAt: ISO8601DateTime!
    $endAt: ISO8601DateTime
    $allDay: Boolean
    $location: String
    $color: String
    $reminderMinutes: [Int!]
    $participantIds: [ID!]
  ) {
    createCalendarEvent(
      projectId: $projectId
      title: $title
      description: $description
      startAt: $startAt
      endAt: $endAt
      allDay: $allDay
      location: $location
      color: $color
      reminderMinutes: $reminderMinutes
      participantIds: $participantIds
    ) {
      calendarEvent {
        id
        title
        startAt
        endAt
        allDay
        color
        reminderMinutes
      }
      errors
    }
  }
`

const CREATE_EVENT_SERIES = gql`
  mutation CreateCalendarEventSeries(
    $projectId: ID
    $title: String!
    $description: String
    $location: String
    $color: String
    $taskId: ID
    $allDay: Boolean
    $reminderMinutes: [Int!]
    $rangeStartDate: ISO8601Date!
    $rangeEndDate: ISO8601Date!
    $weekdays: [Int!]!
    $timeZone: String
    $startTimeSeconds: Int
    $durationMinutes: Int
    $participantIds: [ID!]
  ) {
    createCalendarEventSeries(
      projectId: $projectId
      title: $title
      description: $description
      location: $location
      color: $color
      taskId: $taskId
      allDay: $allDay
      reminderMinutes: $reminderMinutes
      rangeStartDate: $rangeStartDate
      rangeEndDate: $rangeEndDate
      weekdays: $weekdays
      timeZone: $timeZone
      startTimeSeconds: $startTimeSeconds
      durationMinutes: $durationMinutes
      participantIds: $participantIds
    ) {
      calendarEventSeries {
        id
      }
      errors
    }
  }
`

const UPDATE_EVENT = gql`
  mutation UpdateCalendarEvent(
    $id: ID!
    $title: String
    $description: String
    $startAt: ISO8601DateTime
    $endAt: ISO8601DateTime
    $allDay: Boolean
    $location: String
    $color: String
    $reminderMinutes: [Int!]
    $participantIds: [ID!]
    $resendParticipantIds: [ID!]
  ) {
    updateCalendarEvent(
      id: $id
      title: $title
      description: $description
      startAt: $startAt
      endAt: $endAt
      allDay: $allDay
      location: $location
      color: $color
      reminderMinutes: $reminderMinutes
      participantIds: $participantIds
      resendParticipantIds: $resendParticipantIds
    ) {
      calendarEvent {
        id
        title
        startAt
        endAt
        allDay
        color
        reminderMinutes
      }
      errors
    }
  }
`

const UPDATE_EVENT_SERIES = gql`
  mutation UpdateCalendarEventSeries(
    $id: ID!
    $scope: CalendarEventSeriesScopeEnum
    $effectiveDate: ISO8601Date
    $calendarEventId: ID
    $title: String
    $description: String
    $location: String
    $color: String
    $allDay: Boolean
    $reminderMinutes: [Int!]
    $timeZone: String
    $startTimeSeconds: Int
    $durationMinutes: Int
    $participantIds: [ID!]
    $startAt: ISO8601DateTime
    $endAt: ISO8601DateTime
  ) {
    updateCalendarEventSeries(
      id: $id
      scope: $scope
      effectiveDate: $effectiveDate
      calendarEventId: $calendarEventId
      title: $title
      description: $description
      location: $location
      color: $color
      allDay: $allDay
      reminderMinutes: $reminderMinutes
      timeZone: $timeZone
      startTimeSeconds: $startTimeSeconds
      durationMinutes: $durationMinutes
      participantIds: $participantIds
      startAt: $startAt
      endAt: $endAt
    ) {
      calendarEventSeries {
        id
      }
      errors
    }
  }
`

const DELETE_EVENT = gql`
  mutation DestroyCalendarEvent($id: ID!) {
    destroyCalendarEvent(id: $id) {
      calendarEventId
      errors
    }
  }
`

const DELETE_EVENT_SERIES = gql`
  mutation DestroyCalendarEventSeries(
    $id: ID!
    $scope: CalendarEventSeriesScopeEnum
    $effectiveDate: ISO8601Date
    $calendarEventId: ID
  ) {
    destroyCalendarEventSeries(
      id: $id
      scope: $scope
      effectiveDate: $effectiveDate
      calendarEventId: $calendarEventId
    ) {
      calendarEventSeriesId
      errors
    }
  }
`

const { mutate: createEvent } = useMutation(CREATE_EVENT)
const { mutate: createEventSeries } = useMutation(CREATE_EVENT_SERIES)
const { mutate: updateEvent } = useMutation(UPDATE_EVENT)
const { mutate: deleteEvent } = useMutation(DELETE_EVENT)
const { mutate: updateEventSeries } = useMutation(UPDATE_EVENT_SERIES)
const { mutate: deleteEventSeries } = useMutation(DELETE_EVENT_SERIES)

// Get current user
const { currentUser, userId } = useCurrentUser()

const RESPOND_TO_EVENT = gql`
  mutation RespondToCalendarEvent(
    $id: ID!
    $responseStatus: String!
    $scope: CalendarEventResponseScopeEnum
  ) {
    respondToCalendarEvent(id: $id, responseStatus: $responseStatus, scope: $scope) {
      eventParticipant {
        id
        role
        responseStatus
      }
      errors
    }
  }
`

const { mutate: respondToEvent } = useMutation(RESPOND_TO_EVENT)
const responding = ref(false)
const {
  load: loadUserById,
  result: userByIdResult,
  loading: loadingUserById,
} = useLazyQuery(
  GET_USER_BY_ID,
  () => ({
    id: selectedUserToAdd.value,
  }),
  {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  }
)

// Watch for user by ID result
watch(
  () => userByIdResult.value,
  data => {
    // Only process when not loading and we have data
    if (!loadingUserById.value && data?.user && selectedUserToAdd.value) {
      const userId = String(selectedUserToAdd.value)

      // Double-check to prevent duplicates
      if (!isParticipantSelected(userId)) {
        // When adding a participant during editing, always treat them as new (pending)
        // Don't use old status from eventParticipants - they're being re-invited
        const participantToAdd = {
          ...data.user,
          _responseStatus: 'pending', // Always pending for newly added participants
        }
        selectedParticipants.value.push(participantToAdd)
        syncParticipantIds()
        // Clear selection after adding
        nextTick(() => {
          selectedUserToAdd.value = null
        })
      } else {
        // Already selected, just clear the selection
        nextTick(() => {
          selectedUserToAdd.value = null
        })
      }
    }
  },
  { immediate: true, deep: true }
)

// Query project members when project is selected
const projectIdRef = computed(() => form.value.projectId)
const shouldLoadProjectMembers = computed(() => {
  return props.visible && !!projectIdRef.value
})
const { result: projectMembersResult } = useQuery(
  GET_PROJECT_MEMBERS,
  () => ({
    projectId: projectIdRef.value,
  }),
  () => ({
    enabled: shouldLoadProjectMembers.value,
    fetchPolicy: 'cache-first',
  })
)

// Computed: available users for participant selection
const availableUsers = computed(() => {
  if (form.value.projectId && projectMembersResult.value?.project?.members) {
    // For project events, show project members (excluding already selected participants)
    const selectedIds = new Set(form.value.participantIds.map(id => String(id)))
    return projectMembersResult.value.project.members.filter(
      member => !selectedIds.has(String(member.id))
    )
  }
  // For personal events, return empty array (users can search via UserSelect)
  return []
})

async function submit() {
  if (!isValid.value) return

  loading.value = true
  error.value = null

  try {
    // Sync participantIds from selectedParticipants before submit (single source of truth)
    syncParticipantIds()

    // Get participant IDs from selectedParticipants (always the source of truth)
    const participantIds = selectedParticipants.value
      .filter(p => p && p.id) // Filter out any invalid entries
      .map(p => String(p.id))

    // Send empty array instead of null to ensure backend processes participant removal
    // Backend only processes participant_ids if the key exists in params
    const participantIdsValue = participantIds.length > 0 ? participantIds : []

    // Determine which participants should have invitations resent
    // (those that were removed and then re-added)
    const resendParticipantIds = participantIds.filter(id => removedParticipantIds.value.has(id))

    const variables = {
      title: form.value.title.trim(),
      description: form.value.description || null,
      startAt: buildStartAt(),
      endAt: buildEndAt(),
      allDay: form.value.allDay,
      location: form.value.location || null,
      color: form.value.color,
      reminderMinutes: form.value.reminderMinutes,
      participantIds: participantIdsValue,
    }

    // Only include resendParticipantIds if editing and there are participants to resend
    if (isEditing.value && resendParticipantIds.length > 0) {
      variables.resendParticipantIds = resendParticipantIds
    }

    let result
    if (isEditing.value) {
      if (shouldOfferSeriesScope.value) {
        const scope = await requestSeriesScope('update')
        if (!scope) return

        const seriesVariables = buildSeriesUpdateVariables(scope, participantIdsValue)
        result = await updateEventSeries({
          id: props.event.seriesId,
          ...seriesVariables,
        })

        if (result.data?.updateCalendarEventSeries?.errors?.length > 0) {
          error.value = result.data.updateCalendarEventSeries.errors.join(', ')
        } else if (result.data?.updateCalendarEventSeries?.calendarEventSeries) {
          removedParticipantIds.value.clear()
          emit('saved', null)
          close()
        } else {
          error.value = s__('CalendarEvent|Failed to update series. Please try again.')
        }
      } else {
        result = await updateEvent({
          id: props.event.id,
          ...variables,
        })

        if (result.data?.updateCalendarEvent?.errors?.length > 0) {
          error.value = result.data.updateCalendarEvent.errors.join(', ')
        } else if (result.data?.updateCalendarEvent?.calendarEvent) {
          // Clear removed participants tracking after successful save
          removedParticipantIds.value.clear()
          emit('saved', result.data.updateCalendarEvent.calendarEvent)
          close()
        } else {
          error.value = s__('CalendarEvent|Failed to update event. Please try again.')
        }
      }
    } else {
      if (form.value.recurringEnabled) {
        const seriesTimeZone = props.timezone || 'UTC'
        const startDate = new Date(buildStartAt())
        const endDate = buildEndAt() ? new Date(buildEndAt()) : null

        const timeParts = extractTimePartsInTimeZone(startDate, seriesTimeZone)
        const startTimeSeconds = form.value.allDay
          ? null
          : timeParts.hour * 3600 + timeParts.minute * 60 + timeParts.second

        let durationMinutes = null
        if (!form.value.allDay && endDate) {
          durationMinutes = Math.max(
            1,
            Math.round((endDate.getTime() - startDate.getTime()) / 60000)
          )
        }

        result = await createEventSeries({
          projectId: form.value.projectId,
          title: form.value.title.trim(),
          description: form.value.description || null,
          location: form.value.location || null,
          color: form.value.color,
          allDay: form.value.allDay,
          reminderMinutes: form.value.reminderMinutes,
          rangeStartDate: form.value.rangeStartDate,
          rangeEndDate: form.value.rangeEndDate,
          weekdays: form.value.weekdays,
          timeZone: seriesTimeZone,
          startTimeSeconds,
          durationMinutes,
          participantIds: participantIdsValue,
        })

        if (result.data?.createCalendarEventSeries?.errors?.length > 0) {
          error.value = result.data.createCalendarEventSeries.errors.join(', ')
        } else if (result.data?.createCalendarEventSeries?.calendarEventSeries) {
          emit('saved', null)
          close()
        } else {
          error.value = s__('CalendarEvent|Failed to create series. Please try again.')
        }
      } else {
        result = await createEvent({
          projectId: form.value.projectId,
          ...variables,
        })

        if (result.data?.createCalendarEvent?.errors?.length > 0) {
          error.value = result.data.createCalendarEvent.errors.join(', ')
        } else if (result.data?.createCalendarEvent?.calendarEvent) {
          emit('saved', result.data.createCalendarEvent.calendarEvent)
          close()
        } else {
          error.value = s__('CalendarEvent|Failed to create event. Please try again.')
        }
      }
    }
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}

function extractTimePartsInTimeZone(date, timeZone) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone,
  })

  const parts = formatter.formatToParts(date)
  const partValue = type => Number(parts.find(p => p.type === type)?.value || 0)

  return {
    hour: partValue('hour'),
    minute: partValue('minute'),
    second: partValue('second'),
  }
}

function occurrenceDateValue() {
  if (props.event?.occurrenceDate) return props.event.occurrenceDate
  const start = props.event?.startAt ? new Date(props.event.startAt) : null
  if (!start || Number.isNaN(start.getTime())) return null

  const year = start.getUTCFullYear()
  const month = String(start.getUTCMonth() + 1).padStart(2, '0')
  const day = String(start.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Keep in sync with CalendarEvents::RespondService error strings (GraphQL errors array).
function translateRespondToCalendarEventErrors(errors) {
  if (!errors?.length) return ''
  return errors
    .map(msg => {
      switch (msg) {
        case 'Series response is not available for this event':
          return s__('CalendarEvent|Series response is not available for this event')
        case 'Series response is not available for detached occurrences':
          return s__('CalendarEvent|Series response is not available for detached occurrences')
        case 'Series response is not available for past events':
          return s__('CalendarEvent|Series response is not available for past events')
        default:
          return msg
      }
    })
    .join(', ')
}

function requestSeriesScope(actionType = 'update') {
  if (!shouldOfferSeriesScope.value) {
    return Promise.resolve('single')
  }

  scopeSelectorActionType.value = actionType
  showScopeSelector.value = true

  return new Promise(resolve => {
    scopeResolver = resolve
  })
}

function resolveScope(scope) {
  showScopeSelector.value = false

  if (scopeResolver) {
    scopeResolver(scope)
    scopeResolver = null
  }
}

function buildSeriesUpdateVariables(scope, participantIdsValue) {
  const seriesTimeZone = props.timezone || 'UTC'
  const startAt = buildStartAt()
  const endAt = buildEndAt()
  const startDate = startAt ? new Date(startAt) : null
  const endDate = endAt ? new Date(endAt) : null

  const timeParts =
    startDate && !form.value.allDay
      ? extractTimePartsInTimeZone(startDate, seriesTimeZone)
      : { hour: 0, minute: 0, second: 0 }

  const startTimeSeconds = form.value.allDay
    ? null
    : timeParts.hour * 3600 + timeParts.minute * 60 + timeParts.second
  const durationMinutes =
    form.value.allDay || !startDate || !endDate
      ? null
      : Math.max(1, Math.round((endDate.getTime() - startDate.getTime()) / 60000))

  return {
    scope,
    effectiveDate: occurrenceDateValue(),
    calendarEventId: props.event.id,
    title: form.value.title.trim(),
    description: form.value.description || null,
    location: form.value.location || null,
    color: form.value.color,
    allDay: form.value.allDay,
    reminderMinutes: form.value.reminderMinutes,
    timeZone: seriesTimeZone,
    startTimeSeconds,
    durationMinutes,
    participantIds: participantIdsValue,
    startAt: scope === 'single' ? startAt : null,
    endAt: scope === 'single' ? endAt : null,
  }
}

async function confirmDelete() {
  const endedNote = eventHasEnded.value
    ? '\n\n' + s__('CalendarEvent|This event already ended')
    : ''
  if (
    !confirm(
      s__('CalendarEvent|Delete event?') +
        '\n' +
        s__('CalendarEvent|This action cannot be undone.') +
        endedNote
    )
  ) {
    return
  }

  loading.value = true
  error.value = null

  try {
    let result
    if (shouldOfferSeriesScope.value) {
      const scope = await requestSeriesScope('delete')
      if (!scope) return

      result = await deleteEventSeries({
        id: props.event.seriesId,
        scope,
        effectiveDate: occurrenceDateValue(),
        calendarEventId: props.event.id,
      })

      if (result.data?.destroyCalendarEventSeries?.errors?.length > 0) {
        error.value = result.data.destroyCalendarEventSeries.errors.join(', ')
      } else {
        emit('deleted', props.event.id)
        close()
      }
    } else {
      result = await deleteEvent({ id: props.event.id })

      if (result.data?.destroyCalendarEvent?.errors?.length > 0) {
        error.value = result.data.destroyCalendarEvent.errors.join(', ')
      } else {
        emit('deleted', props.event.id)
        close()
      }
    }
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}

async function handleAccept() {
  if (!props.event || responding.value) return

  responding.value = true
  error.value = null

  try {
    let scope = 'single'
    if (shouldOfferSeriesScope.value) {
      const selectedScope = await requestSeriesScope('respond')
      if (!selectedScope) return
      scope = selectedScope
    }

    const result = await respondToEvent({
      id: props.event.id,
      responseStatus: 'accepted',
      scope,
    })

    if (result.data?.respondToCalendarEvent?.errors?.length > 0) {
      error.value = translateRespondToCalendarEventErrors(result.data.respondToCalendarEvent.errors)
    } else {
      // Emit response event with success message
      emit('responded', {
        success: true,
        message: s__('CalendarEvent|Event invitation accepted'),
        type: 'success',
      })
      // Close modal
      close()
    }
  } catch (err) {
    error.value = err.message || s__('CalendarEvent|Failed to accept event. Please try again.')
  } finally {
    responding.value = false
  }
}

async function handleReject() {
  if (!props.event || responding.value) return

  responding.value = true
  error.value = null

  try {
    let scope = 'single'
    if (shouldOfferSeriesScope.value) {
      const selectedScope = await requestSeriesScope('respond')
      if (!selectedScope) return
      scope = selectedScope
    }

    const result = await respondToEvent({
      id: props.event.id,
      responseStatus: 'declined',
      scope,
    })

    if (result.data?.respondToCalendarEvent?.errors?.length > 0) {
      error.value = translateRespondToCalendarEventErrors(result.data.respondToCalendarEvent.errors)
    } else {
      // Emit response event with success message
      emit('responded', {
        success: true,
        message: s__('CalendarEvent|Event invitation declined'),
        type: 'success',
      })
      // Close modal
      close()
    }
  } catch (err) {
    error.value = err.message || s__('CalendarEvent|Failed to reject event. Please try again.')
  } finally {
    responding.value = false
  }
}

// Handler for attachments changed event (uploaded/deleted)
function handleAttachmentsChanged() {
  // No need to refetch the main event query - attachments are loaded separately
  // The CalendarEventAttachments component handles its own refetch
}
</script>

<style scoped>
/* Component-specific: border-radius override */
.modal-content {
  border-radius: 12px;
  max-width: 480px;
}

.calendar-ended-notice {
  margin-bottom: 12px;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-left: 4px solid #f59e0b;
  border-radius: 8px;
}

.calendar-detached-notice {
  margin-bottom: 12px;
  padding: 10px 12px;
  font-size: 13px;
  color: #1e3a5f;
  background: #eff6ff;
  border: 1px solid #93c5fd;
  border-left: 4px solid #2563eb;
  border-radius: 8px;
}

.calendar-detached-notice-title {
  font-weight: 600;
  margin-bottom: 4px;
  color: #1e40af;
}

.calendar-detached-notice-body {
  font-weight: 500;
  line-height: 1.45;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-delete-header {
  background: none;
  border: none;
  color: #dc3545;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  transition:
    background-color 0.2s,
    color 0.2s;
}

.btn-delete-header:hover:not(:disabled) {
  background-color: #fee;
  color: #c82333;
}

.btn-delete-header:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.form-group {
  margin-bottom: 16px;
  min-width: 0;
}

.recurrence-fields {
  padding: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
}

.recurrence-range {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.recurrence-date-field {
  flex: 1;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.recurrence-range-separator {
  color: #666;
  flex: 0 0 auto;
}

.weekday-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
}

.scope-selector-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  padding: 16px;
}

.scope-selector-modal {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 10px;
  padding: 16px;
}

.scope-selector-title {
  margin: 0 0 12px;
  color: #333;
  font-size: 16px;
}

.scope-selector-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.scope-selector-actions .btn-sm {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  justify-content: flex-start;
}

.scope-selector-action-label {
  font-weight: 500;
}

.scope-selector-action-hint {
  color: #666;
  font-size: 12px;
  line-height: 1.3;
  text-align: left;
}

.scope-selector-footer {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}

.form-label {
  display: block;
  font-weight: 500;
  margin-bottom: 6px;
  color: #333;
  font-size: 14px;
}

.form-input,
.form-select,
.form-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
  color: #333;
  box-sizing: border-box;
}

@media (max-width: 767px) {
  .form-input,
  .form-select,
  .form-textarea {
    font-size: 16px;
  }

  .recurrence-range {
    flex-wrap: wrap;
    align-items: stretch;
  }

  .recurrence-range-separator {
    display: none;
  }

  .recurrence-range-end-input {
    width: 100%;
    flex-basis: 100%;
  }
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: #2aabee;
}

.datetime-row {
  display: flex;
  gap: 8px;
}

.date-input {
  flex: 1;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  display: block;
  box-sizing: border-box;
}

.all-day-date-input {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  inline-size: 100%;
  max-inline-size: 100%;
  -webkit-appearance: none;
  appearance: none;
}

.datetime-input-field {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.datetime-input-field :deep(.dp__input_wrap),
.datetime-input-field :deep(.dp__input) {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

/* iOS Safari keeps intrinsic width unless explicitly clamped */
@supports (-webkit-touch-callout: none) {
  .all-day-date-input {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    inline-size: 100% !important;
    max-inline-size: 100% !important;
  }

  .datetime-input-field,
  .datetime-input-field :deep(.dp__input_wrap),
  .datetime-input-field :deep(.dp__input) {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
  }
}

.time-input {
  width: 120px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
}

.checkbox-label input[type='checkbox'] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.color-picker {
  display: flex;
  align-items: center;
  gap: 16px;
}

.color-selected {
  display: flex;
  align-items: center;
  gap: 8px;
}

.color-selected-label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin: 0;
}

.color-selected-display {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  border: 2px solid #ddd;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.color-options {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.color-btn {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  background: none;
  padding: 0;
}

.color-btn:hover:not(:disabled) {
  transform: scale(1.1);
  border-color: #333;
}

.color-btn.selected {
  border-color: #333;
  box-shadow: 0 0 0 2px #fff;
}

.color-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.color-custom {
  display: flex;
  align-items: center;
}

.color-custom input[type='color'] {
  width: 28px;
  height: 28px;
  padding: 2px;
  border: 2px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
}

.error-message {
  background: #ffebee;
  color: #c62828;
  padding: 12px;
  border-radius: 6px;
  margin-top: 16px;
  font-size: 14px;
}

/* Component-specific: footer layout */
.modal-footer {
  justify-content: space-between;
}

.footer-left {
  display: flex;
  gap: 12px;
}

.footer-right {
  display: flex;
  gap: 12px;
}

.btn-accept {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.15s ease;
  background: #0f9d58;
  color: #fff;
  border: none;
}

.btn-accept:hover:not(:disabled) {
  opacity: 0.9;
}

.btn-accept:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-reject {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.15s ease;
  background: #db4437;
  color: #fff;
  border: none;
}

.btn-reject:hover:not(:disabled) {
  background: #c53030;
  opacity: 1;
}

.btn-reject:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary,
.btn-secondary,
.btn-danger {
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.response-btn-icon {
  display: none;
  line-height: 0;
}

@media (max-width: 767px) {
  .date-input {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .footer-left {
    gap: 8px;
  }

  .response-btn-label {
    display: none;
  }

  .response-btn-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .btn-accept,
  .btn-reject {
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    padding: 6px;
    border-radius: 8px;
  }
}

.btn-primary {
  background: #2aabee;
  color: #fff;
  border: none;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: transparent;
  color: #2aabee;
  border: 1px solid #2aabee;
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-danger {
  background: transparent;
  color: #dc3545;
  border: 1px solid #dc3545;
}

.btn-danger:hover {
  background: #dc3545;
  color: #fff;
}

.btn-danger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Reminders styles */
.participants-list {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 8px;
  margin-bottom: 8px;
  min-height: 36px;
}

.participant-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  background: #fff;
  border-radius: 4px;
  margin-bottom: 4px;
}

.participant-item:last-child {
  margin-bottom: 0;
}

.participant-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.participant-name-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.participant-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;
}

.participant-status-accepted {
  background-color: #22c55e;
}

.participant-status-declined {
  background-color: #ef4444;
}

.participant-status-pending {
  background-color: #9ca3af;
}

.participant-username {
  font-size: 13px;
  font-weight: 500;
  color: #333;
}

.participant-name {
  font-size: 12px;
  color: #666;
}

.participant-fallback {
  font-size: 13px;
  color: #666;
}

.participant-remove {
  background: none;
  border: none;
  color: #dc2626;
  font-size: 20px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
}

.participant-remove:hover {
  background-color: #dc2626;
  color: #fff;
}

.no-participants {
  padding: 8px;
  text-align: center;
  color: #999;
  font-size: 13px;
}

.organizer-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  background: #f0f7ff;
  border: 1px solid #d0e7ff;
  border-radius: 8px;
}

.organizer-name-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.organizer-username {
  font-size: 13px;
  font-weight: 500;
  color: #333;
}

.organizer-name {
  font-size: 12px;
  color: #666;
}

.organizer-fallback {
  font-size: 13px;
  color: #666;
}

.add-participant {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.add-participant .user-select-wrapper {
  flex: 1;
}

.btn-add-participant {
  padding: 8px 16px;
  background: #2aabee;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}

.btn-add-participant:hover:not(:disabled) {
  opacity: 0.9;
}

.btn-add-participant:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.reminders-list {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 8px;
  margin-bottom: 8px;
  min-height: 36px;
}

.reminder-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  background: #fff;
  border-radius: 4px;
  margin-bottom: 4px;
}

.reminder-item:last-child {
  margin-bottom: 0;
}

.reminder-text {
  font-size: 13px;
  color: #333;
}

.reminder-remove {
  background: none;
  border: none;
  color: #dc2626;
  font-size: 20px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
}

.reminder-remove:hover {
  background-color: #dc2626;
  color: #fff;
}

.no-reminders {
  font-size: 13px;
  color: #999;
  text-align: center;
  padding: 8px;
}

.add-reminder {
  display: flex;
  gap: 8px;
}

.reminder-select {
  flex: 1;
}

.btn-add-reminder {
  padding: 10px 16px;
  background: #2aabee;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.btn-add-reminder:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
